import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Row,
  Typography,
} from "antd";
import { Descriptions } from "antd/es";
import { getData, postData } from "apiServices/common";
import AppPageHeader from "common/PageHeader";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useLocation } from "react-router-dom";

const UpdateSewingToFolding = () => {
  const [sewingDetails, setSewingDetails] = useState();
  const [sewingEntries, setSewingEntries] = useState();
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [mismatchQuantity, setMismatchQuantity] = useState(0);
  const [validationError, setValidationError] = useState(false);

  //   Routes
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  //   Antd Constants
  const [form] = Form.useForm();

  // Calculate mismatch quantity based on form values
  const calculateMismatch = (values) => {
    const entries = values?.sewingEntries || [];
    const total = entries.reduce(
      (sum, entry) => sum + (parseInt(entry.quantity) || 0),
      0,
    );
    setTotalQuantity(total);

    const mismatch = (sewingDetails?.sewing_send_quantity || 0) - total;
    setMismatchQuantity(mismatch);

    // Update validation error state
    setValidationError(total > (sewingDetails?.sewing_send_quantity || 0));

    return { total, mismatch };
  };

  // Listen for quantity changes in form fields
  const handleQuantityChange = () => {
    const values = form.getFieldsValue();
    const { total } = calculateMismatch(values);

    // Add validation warning for real-time feedback
    if (total > sewingDetails?.sewing_send_quantity) {
      message.warning(
        `Total Quantity (${total}) exceeds Total Sewing Send Quantity (${sewingDetails.sewing_send_quantity})`,
      );
    }
  };

  const handleUpdate = async () => {
    try {
      const formValues = form.getFieldsValue();
      const { total, mismatch } = calculateMismatch(formValues);

      // Add validation to ensure total quantity doesn't exceed sewing send quantity
      if (total > sewingDetails.sewing_send_quantity) {
        message.error(
          `Total Quantity (${total}) cannot exceed Total Sewing Send Quantity (${sewingDetails.sewing_send_quantity})`,
        );
        return;
      }

      const details = formValues.sewingEntries.map((entry, index) => ({
        id: sewingEntries[index].id,
        quantity: entry.quantity,
      }));

      const payload = {
        line_id: sewingDetails.line_info?.id,
        style_id: sewingDetails.style_info?.id,
        sales_contract_id: sewingDetails.sales_contract_info?.id,
        buying_office_id: sewingDetails.buying_office_info?.id || null,
        buyer_id: sewingDetails.buyer_info?.id || null,
        sub_contract_factory_id:
          sewingDetails.sub_contract_factory_info?.id || null,
        sub_style_id: sewingDetails.sub_style_info?.id || null,
        po_map_id: sewingDetails.po_map_info?.id || null,
        color_id: sewingDetails.color_info?.id,
        size_id: sewingDetails.size_info?.id,
        // total_qty: sewingDetails.sewing_send_quantity,
        sewing_send_quantity: sewingDetails.sewing_send_quantity,
        quantity: total,
        mismatch_quantity: mismatch,
        status: "received",
        details: details,
      };

      const response = await postData(
        `/api/digitalization/single-date-wise-update/${id}`,
        payload,
      );

      if (response?.code === 200) {
        message.success("Sewing to Folding details updated successfully");
        navigate("/digitalization/finishing/sewing-to-folding");
      } else {
        message.error("Failed to update details");
      }
    } catch (error) {
      message.error("Failed to update details");
    }
  };

  const extra = (
    <div className="extra-button-container">
      <Button
        size="small"
        htmlType="submit"
        style={{ marginRight: 5 }}
        onClick={() => {
          form.resetFields();
          navigate("/digitalization/finishing/sewing-to-folding");
        }}
      >
        {" "}
        Back
      </Button>
      <Button
        type="primary"
        size="small"
        style={{ marginRight: 5 }}
        onClick={handleUpdate}
        disabled={validationError}
      >
        {" "}
        Save
      </Button>
    </div>
  );

  useEffect(() => {
    if (id) {
      const fetchSewingDetails = async () => {
        try {
          const response = await getData(
            `/api/digitalization/single-date-wise-update-view/${id}`,
            null,
            {},
          );
          const data = response.data?.data;
          setSewingDetails(data);
          setSewingEntries(data.garment_sewing_to_finishing_details);

          const entries = data.garment_sewing_to_finishing_details.map(
            (item) => ({
              entry_time: item.entry_time,
              quantity: item.quantity,
              user_id: item.created_by ? `${item.created_by.name}` : "N/A",
            }),
          );

          form.setFieldsValue({
            sewingEntries: entries,
          });

          // Initialize total and mismatch quantities
          const total = entries.reduce(
            (sum, entry) => sum + (parseInt(entry.quantity) || 0),
            0,
          );
          setTotalQuantity(total);
          const mismatch = (data.sewing_send_quantity || 0) - total;
          setMismatchQuantity(mismatch);
        } catch (error) {
          message.error("Failed to fetch Sewing to Folding details");
          setSewingDetails(null);
        }
      };
      fetchSewingDetails();
    }
  }, [id, form]);

  return (
    <>
      <AppPageHeader
        title={
          <Typography.Title level={5}>
            Update Finishing (Sewing to Folding)
          </Typography.Title>
        }
        extra={extra}
      />

      <Layout.Content className="item-details">
        <Card
          title="Finishing (Sewing to Folding) Details"
          bordered={false}
          style={{ marginTop: 5 }}
        >
          {sewingDetails && (
            <>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Date</strong>
                    </div>
                    <div>
                      {dayjs(sewingDetails.entry_date).format("YYYY-MM-DD")}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Line</strong>
                    </div>
                    <div>{sewingDetails.line_info?.name || "N/A"}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Buyer</strong>
                    </div>
                    <div>{sewingDetails.buyer_info?.name || "N/A"}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Style</strong>
                    </div>
                    <div>{sewingDetails.style_info?.style_no || "N/A"}</div>
                  </div>
                </Col>

                <Col span={6}>
                  <div>
                    <div>
                      <strong>Sub Style</strong>
                    </div>
                    <div>
                      {sewingDetails.sub_style_info?.sub_style_no || "N/A"}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Po Map</strong>
                    </div>
                    <div>{sewingDetails.po_map_info?.original_po || "N/A"}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Color</strong>
                    </div>
                    <div>{sewingDetails.color_info?.name || "N/A"}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Size</strong>
                    </div>
                    <div>{sewingDetails.size_info?.name || "N/A"}</div>
                  </div>
                </Col>

                <Col span={6}>
                  <div>
                    <div>
                      <strong>Sales Contract</strong>
                    </div>
                    <div>
                      {sewingDetails.sales_contract_info?.reference_no || "N/A"}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Buying Office</strong>
                    </div>
                    <div>{sewingDetails.buying_office_info?.name || "N/A"}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Total Quantity</strong>
                    </div>
                    <div
                      style={{
                        color: validationError ? "red" : "inherit",
                        fontWeight: validationError ? "bold" : "normal",
                      }}
                    >
                      {totalQuantity || "0"}
                    </div>
                  </div>
                </Col>
                {/* <Col span={6}>
                  <div>
                    <div>
                      <strong>Total Sewing Send Quantity</strong>
                    </div>
                    <div>{sewingDetails.sewing_send_quantity || "N/A"}</div>
                  </div>
                </Col> */}
                <Col span={6}>
                  <div>
                    <div>
                      <strong>Mismatch</strong>
                    </div>
                    <div
                      style={{
                        color: validationError ? "red" : "inherit",
                      }}
                    >
                      {mismatchQuantity || "0"}
                    </div>
                  </div>
                </Col>
              </Row>
              {validationError && (
                <Row>
                  <Col span={24}>
                    <div
                      style={{
                        marginTop: 16,
                        padding: "8px 16px",
                        backgroundColor: "#fff2f0",
                        border: "1px solid #ffccc7",
                        borderRadius: 2,
                        color: "#ff4d4f",
                      }}
                    >
                      <strong>Error:</strong> Total Quantity ({totalQuantity})
                      exceeds Total Sewing Send Quantity (
                      {sewingDetails.sewing_send_quantity}). Please adjust the
                      quantities to proceed.
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Card>

        <Card
          title="Entry Details"
          style={{ marginTop: 20, boxShadow: "none" }}
          bordered={false}
        >
          <Form form={form} layout="vertical">
            <Form.List name="sewingEntries">
              {(fields) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => {
                    return (
                      <Card
                        key={key}
                        style={{ marginTop: 10, boxShadow: "none" }}
                      >
                        <Row gutter={24} align="middle">
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, "user_id"]}
                              fieldKey={[fieldKey, "user_id"]}
                              label="Entry By"
                            >
                              <Input disabled />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Entry Time"
                              {...restField}
                              name={[name, "entry_time"]}
                              fieldKey={[fieldKey, "entry_time"]}
                            >
                              <Input disabled />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, "quantity"]}
                              fieldKey={[fieldKey, "quantity"]}
                              label="Quantity"
                              validateStatus={
                                validationError ? "error" : undefined
                              }
                              help={
                                validationError ? "Total exceeds limit" : ""
                              }
                            >
                              <InputNumber
                                style={{
                                  width: "100%",
                                  borderColor: validationError
                                    ? "#ff4d4f"
                                    : undefined,
                                }}
                                min={0}
                                onChange={handleQuantityChange}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    );
                  })}
                </>
              )}
            </Form.List>
          </Form>
        </Card>
      </Layout.Content>
    </>
  );
};

export default UpdateSewingToFolding;
